'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import { closeResponsiveDialog, useResponsiveDialogStore } from '@/stores/ResponsiveDialogStore';
const ResponsiveDialog = dynamic(() => import('../../organism/ResponsiveDialog').then(mod => ({
  default: mod.ResponsiveDialog
})), {
  ssr: false
});

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * openResponsiveDialog를 이용해서 간결하게 사용 가능
 *
 * @example
 *
 * ```tsx
 * import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
 *
 * openResponsiveDialog({
 *   component: HouseholdConsent,
 * });
 * ```
 */
export const ResponsiveDialogContainer = () => {
  const $ = _c(24);
  const {
    currentDialog,
    isOpen
  } = useResponsiveDialogStore();
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();
  const openedPathnameRef = useRef(undefined);
  const isDialogOpen = isOpen && !!currentDialog;
  const requestClose = _temp;
  useOverlayHistory(isDialogOpen, requestClose);
  let t0;
  let t1;
  if ($[0] !== isDialogOpen || $[1] !== pathname) {
    t0 = () => {
      if (!isDialogOpen) {
        openedPathnameRef.current = undefined;
        return;
      }
      const currentPathname = pathname ?? "";
      if (openedPathnameRef.current === undefined) {
        openedPathnameRef.current = currentPathname;
        return;
      }
      if (openedPathnameRef.current !== currentPathname) {
        closeResponsiveDialog();
      }
    };
    t1 = [isDialogOpen, pathname];
    $[0] = isDialogOpen;
    $[1] = pathname;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMounted(true);
    };
    t3 = [];
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== isOpen) {
    t4 = () => {
      const handleKeyDown = event => {
        if (event.key === "Escape" && isOpen) {
          requestClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleKeyDown);
      }
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t5 = [isOpen, requestClose];
    $[6] = isOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  if (!isMounted || !currentDialog) {
    return null;
  }
  const Component = currentDialog.component;
  let t6;
  if ($[9] !== Component || $[10] !== currentDialog.props) {
    t6 = <Component {...currentDialog.props} onClose={requestClose} />;
    $[9] = Component;
    $[10] = currentDialog.props;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== currentDialog.ariaLabel || $[13] !== currentDialog.className || $[14] !== currentDialog.drawerNoPadding || $[15] !== currentDialog.drawerType || $[16] !== currentDialog.duration || $[17] !== currentDialog.forceModal || $[18] !== currentDialog.hideDrawerHeader || $[19] !== currentDialog.modalHeader || $[20] !== currentDialog.title || $[21] !== isOpen || $[22] !== t6) {
    t7 = <ResponsiveDialog opened={isOpen} onClose={requestClose} skipOverlayHistory={true} title={currentDialog.title} ariaLabel={currentDialog.ariaLabel} duration={currentDialog.duration} className={currentDialog.className} modalHeader={currentDialog.modalHeader} drawerNoPadding={currentDialog.drawerNoPadding} hideDrawerHeader={currentDialog.hideDrawerHeader} drawerType={currentDialog.drawerType} forceModal={currentDialog.forceModal}>{t6}</ResponsiveDialog>;
    $[12] = currentDialog.ariaLabel;
    $[13] = currentDialog.className;
    $[14] = currentDialog.drawerNoPadding;
    $[15] = currentDialog.drawerType;
    $[16] = currentDialog.duration;
    $[17] = currentDialog.forceModal;
    $[18] = currentDialog.hideDrawerHeader;
    $[19] = currentDialog.modalHeader;
    $[20] = currentDialog.title;
    $[21] = isOpen;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
};
function _temp() {
  closeResponsiveDialog();
}